import { ReportSection } from "../ReportSection";
import { ReportTable, type TableColumn } from "../ReportTable";
import { StatCard } from "../StatCard";
import { fmtPercent } from "./formatters";

interface IdeasData {
  funnel: {
    captured: number;
    exploring: number;
    adopted: number;
    rejected: number;
    onHold: number;
  };
  conversionRate: number;
  topIdeas: Array<{
    id: string;
    code: string;
    title: string;
    votesUp: number;
    votesDown: number;
    netVotes: number;
    status: string;
  }>;
  byCategory: Array<{ category: string; count: number; adoptedCount: number }>;
}

const FUNNEL_STAGES: Array<{
  key: keyof IdeasData["funnel"];
  label: string;
  color: string;
}> = [
  { key: "captured", label: "Captured", color: "hsl(210,16%,80%)" },
  { key: "exploring", label: "Exploring", color: "hsl(210,100%,50%)" },
  { key: "adopted", label: "Adopted", color: "hsl(142,72%,40%)" },
];

function rankColor(rank: number): string {
  if (rank === 1) return "hsl(45,90%,45%)";
  if (rank === 2) return "hsl(0,0%,55%)";
  if (rank === 3) return "hsl(25,75%,40%)";
  return "hsl(215,16%,40%)";
}

interface IdeaRow {
  id: string;
  code: string;
  title: string;
  votesUp: number;
  votesDown: number;
  netVotes: number;
  status: string;
}

interface CategoryRow {
  category: string;
  count: number;
  adoptedCount: number;
}

export function IdeasPipelineReport({ data }: { data: IdeasData }) {
  const totalForFunnel =
    data.funnel.captured + data.funnel.exploring + data.funnel.adopted;

  const ranked = data.topIdeas.map((r, idx) => ({ ...r, rankIdx: idx + 1 }));

  const colsWithRank: Array<TableColumn<IdeaRow & { rankIdx: number }>> = [
    {
      key: "rank",
      label: "Rank",
      width: 60,
      render: (r) => (
        <span style={{ color: rankColor(r.rankIdx), fontWeight: 700 }}>
          #{r.rankIdx}
        </span>
      ),
    },
    {
      key: "title",
      label: "Title",
      render: (r) => <span className="font-medium text-foreground">{r.title}</span>,
    },
    {
      key: "status",
      label: "Status",
      render: (r) => (
        <span className="capitalize">{r.status.replace(/_/g, " ")}</span>
      ),
    },
    {
      key: "netVotes",
      label: "Net Votes",
      align: "right",
      render: (r) => (
        <span>
          {r.netVotes > 0 ? `+${r.netVotes}` : r.netVotes}{" "}
          <span className="text-[11px] text-muted-foreground">
            (▲{r.votesUp} ▼{r.votesDown})
          </span>
        </span>
      ),
    },
  ];

  const categoryCols: Array<TableColumn<CategoryRow>> = [
    { key: "category", label: "Category" },
    { key: "count", label: "Submitted", align: "right" },
    { key: "adoptedCount", label: "Adopted", align: "right" },
    {
      key: "rate",
      label: "Rate",
      align: "right",
      render: (r) => (r.count > 0 ? fmtPercent((r.adoptedCount / r.count) * 100) : "—"),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label="Captured" value={data.funnel.captured} />
        <StatCard label="Exploring" value={data.funnel.exploring} />
        <StatCard label="Adopted" value={data.funnel.adopted} tone="good" />
        <StatCard label="Rejected" value={data.funnel.rejected} tone="bad" />
        <StatCard
          label="Conversion Rate"
          value={fmtPercent(data.conversionRate)}
          tone={data.conversionRate > 30 ? "good" : "warn"}
        />
      </div>

      <ReportSection title="Pipeline Funnel">
        <div className="space-y-2">
          {FUNNEL_STAGES.map((s, idx) => {
            const count = data.funnel[s.key];
            const width =
              totalForFunnel > 0 ? (count / totalForFunnel) * 100 : 0;
            const fromPrev = idx === 0 ? null : data.funnel[FUNNEL_STAGES[idx - 1].key];
            const conv =
              fromPrev !== null && fromPrev > 0
                ? Math.round((count / fromPrev) * 100)
                : null;
            return (
              <div key={s.key}>
                <div className="mb-1 flex items-center justify-between text-[12px]">
                  <span className="font-medium">{s.label}</span>
                  <span className="text-muted-foreground">
                    {count}
                    {conv !== null && (
                      <span className="ml-2 text-[11px]">
                        ({conv}% from prev)
                      </span>
                    )}
                  </span>
                </div>
                <div
                  className="h-7 rounded-md transition-all"
                  style={{
                    width: `${Math.max(8, width)}%`,
                    background: s.color,
                  }}
                />
              </div>
            );
          })}
        </div>
      </ReportSection>

      <ReportSection title="Top Ideas">
        <ReportTable
          columns={colsWithRank}
          rows={ranked}
          rowKey={(r) => r.id}
          empty="No ideas to rank."
        />
      </ReportSection>

      <ReportSection title="By Category">
        <ReportTable
          columns={categoryCols}
          rows={data.byCategory}
          rowKey={(r, i) => `${r.category}-${i}`}
        />
      </ReportSection>
    </div>
  );
}
